export type RunMode = 'CAMPAIGN' | 'ENDLESS' | 'DAILY_CHALLENGE'

export interface RunModeDefinition {
  readonly id: RunMode
  readonly name: string
  readonly codename: string
  readonly glyph: string
  readonly description: string
  readonly rules: string
  readonly color: string
}

export interface DailyChallenge {
  readonly id: string
  readonly dateKey: string
  readonly seed: number
  readonly name: string
  readonly briefing: string
  readonly scoreMultiplier: number
  readonly xpBonus: number
}

export const RUN_MODES: Record<RunMode, RunModeDefinition> = {
  CAMPAIGN: {
    id: 'CAMPAIGN', name: 'CAMPAIGN RUN', codename: 'STORY', glyph: '◇', color: '#35d8ea',
    description: 'Secure five authored waves and complete the active campaign chapter.',
    rules: '5 WAVES // CHAPTER REWARDS // STANDARD SCORE',
  },
  ENDLESS: {
    id: 'ENDLESS', name: 'ENDLESS LEDGER', codename: 'INFINITE', glyph: '∞', color: '#ffcf76',
    description: 'Continue beyond finality through procedurally escalating blocks and recurring bosses.',
    rules: 'NO WAVE LIMIT // BOSS EVERY 5 // PERSONAL BEST',
  },
  DAILY_CHALLENGE: {
    id: 'DAILY_CHALLENGE', name: 'DAILY HASH', codename: 'SEEDED', glyph: '#', color: '#ff4fd8',
    description: 'A deterministic five-wave run shared by the same local date and challenge seed.',
    rules: '+1 THREAT // -10% CAPITAL // 1.25× SCORE',
  },
}

export const RUN_MODE_IDS = Object.keys(RUN_MODES) as RunMode[]

const CHALLENGE_NAMES = ['ZERO-DAY FORK', 'HASH COLLISION', 'DARK LIQUIDITY', 'ORPHAN BLOCK', 'BYZANTINE CLOCK'] as const

export function getDailyChallenge(date = new Date()): DailyChallenge {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const dateKey = `${year}-${month}-${day}`
  const dayIndex = Math.floor(Date.UTC(year, date.getMonth(), date.getDate()) / 86_400_000)
  const seed = Math.abs((dayIndex * 48_271 + 17_389) % 900_000) + 100_000
  const name = CHALLENGE_NAMES[Math.abs(dayIndex) % CHALLENGE_NAMES.length]
  return {
    id: `HASH-${dateKey}`,
    dateKey,
    seed,
    name,
    briefing: `Deterministic threat lattice locked to seed ${seed}. All operators receive the same campaign condition.`,
    scoreMultiplier: 1.25,
    xpBonus: 125,
  }
}
